/**
 * Test DHIS2 / AmbX live ASR — same /asr lifecycle as AmbientListeningContext:
 * empty end frame (browser) or STOP (hardware), processing + batch_progress, ready_to_stop,
 * final_diarized_transcript. Mic visuals match AmbientMicButton (asset icons + determinate progress).
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Paper, Alert, Tooltip, CircularProgress, LinearProgress } from '@mui/material';
import { useTranscript } from '../../contexts/TranscriptContext';
import type { AmbientProcessingProgress } from '../../contexts/AmbientListeningContext';
import { getWebSocketUrl } from '../../utils/websocket';

export type ListeningState = 'idle' | 'listening' | 'processing';
export type AudioSourceType = 'browser' | 'hardware';

interface AmbXListenerProps {
  sessionId: string;
  audioSourceType?: AudioSourceType;
  onStart?: () => void;
  onStop?: () => void;
  disabled?: boolean;
}

export default function AmbXListener({
  sessionId,
  audioSourceType = 'browser',
  onStart,
  onStop,
  disabled = false,
}: AmbXListenerProps) {
  const [state, setState] = useState<ListeningState>('idle');
  const [processingProgress, setProcessingProgress] = useState<AmbientProcessingProgress | null>(null);
  const [awaitingWsClose, setAwaitingWsClose] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const { startListening: startTranscriptListening, stopListening: stopTranscriptListening } = useTranscript();

  const mediaRecorderRef = useRef<{
    audioContext: AudioContext;
    source: MediaStreamAudioSourceNode;
    workletNode: AudioWorkletNode;
    recorderWorker: Worker;
  } | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const keepaliveIntervalRef = useRef<number | null>(null);
  const userStoppedRef = useRef(false);
  /** From backend `/asr` config/status; default true for older servers. */
  const postStopBatchUiRef = useRef(true);
  const stateRef = useRef<ListeningState>(state);
  stateRef.current = state;
  const startListeningRef = useRef<(skipClearOldData?: boolean) => Promise<void>>(async () => {});

  const stopListening = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (keepaliveIntervalRef.current) {
      clearInterval(keepaliveIntervalRef.current);
      keepaliveIntervalRef.current = null;
    }

    userStoppedRef.current = true;

    const ws = websocketRef.current;
    const sid = sessionId;

    if (ws && ws.readyState === WebSocket.OPEN) {
      setState('processing');
      setProcessingProgress({ label: 'Processing conversation…', percent: 8 });
      try {
        if (audioSourceType === 'hardware') {
          ws.send('STOP');
        } else {
          ws.send(new Uint8Array(0));
        }
      } catch {
        /* ignore */
      }
    }

    if (mediaRecorderRef.current) {
      const { audioContext, source, workletNode, recorderWorker } = mediaRecorderRef.current;
      try {
        if (recorderWorker) recorderWorker.terminate();
        if (workletNode) {
          workletNode.port.onmessage = null;
          workletNode.disconnect();
        }
        if (source) source.disconnect();
        if (audioContext && audioContext.state !== 'closed') audioContext.close();
      } catch (err) {
        console.error('[AmbX] Error stopping audio:', err);
      }
      mediaRecorderRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (sid && (window as unknown as { __whisperLiveKitData?: unknown }).__whisperLiveKitData) {
      try {
        localStorage.setItem(
          `whisperData_${sid}`,
          JSON.stringify((window as unknown as { __whisperLiveKitData: unknown }).__whisperLiveKitData)
        );
      } catch {
        console.warn('[AmbX] Failed to save to localStorage');
      }
    }

    if (ws && ws.readyState === WebSocket.OPEN) {
      const closeTimer = window.setTimeout(() => {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
        websocketRef.current = null;
        userStoppedRef.current = false;
        setProcessingProgress(null);
        setAwaitingWsClose(false);
        setState('idle');
        setStatusMessage('');
        stopTranscriptListening();
        onStop?.();
      }, 120000);

      const prevOnMessage = ws.onmessage;
      ws.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data as string) as Record<string, unknown>;
          if (data.type === 'batch_progress' && postStopBatchUiRef.current) {
            const label = typeof data.label === 'string' ? data.label : 'Processing…';
            const pct = typeof data.percent === 'number' ? data.percent : 0;
            setProcessingProgress({
              label,
              percent: pct,
              stage: typeof data.stage === 'string' ? data.stage : undefined,
            });
            return;
          }
          if (data.type === 'ready_to_stop') {
            window.clearTimeout(closeTimer);
            try {
              ws.close();
            } catch {
              /* ignore */
            }
            websocketRef.current = null;
            userStoppedRef.current = false;
            setProcessingProgress(null);
            setAwaitingWsClose(false);
            setState('idle');
            setStatusMessage('');
            stopTranscriptListening();
            onStop?.();
            return;
          }
        } catch {
          /* ignore */
        }
        if (typeof prevOnMessage === 'function') prevOnMessage.call(ws, event);
      };
    } else {
      if (websocketRef.current) {
        try {
          websocketRef.current.close();
        } catch {
          /* ignore */
        }
        websocketRef.current = null;
      }
      userStoppedRef.current = false;
      setProcessingProgress(null);
      setAwaitingWsClose(false);
      setState('idle');
      setStatusMessage('');
      stopTranscriptListening();
      onStop?.();
    }
  }, [sessionId, audioSourceType, stopTranscriptListening, onStop]);

  const startListening = useCallback(
    async (skipClearOldData = false) => {
      if (!sessionId) {
        setError('No active session');
        return;
      }

      if (!skipClearOldData) {
        delete (window as unknown as { __whisperLiveKitData?: unknown }).__whisperLiveKitData;
        localStorage.removeItem(`whisperData_${sessionId}`);
      }

      setError(null);
      setProcessingProgress(null);
      setAwaitingWsClose(false);
      postStopBatchUiRef.current = true;
      userStoppedRef.current = false;

      // Clean up any previous audio pipeline before (re)starting to prevent
      // stale workers/contexts sending audio to a dead WebSocket on reconnect.
      if (mediaRecorderRef.current) {
        const { audioContext, source, workletNode, recorderWorker } = mediaRecorderRef.current;
        try {
          if (recorderWorker) recorderWorker.terminate();
          if (workletNode) { workletNode.port.onmessage = null; workletNode.disconnect(); }
          if (source) source.disconnect();
          if (audioContext && audioContext.state !== 'closed') audioContext.close();
        } catch { /* ignore */ }
        mediaRecorderRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }

      try {
        if (audioSourceType === 'hardware') {
          console.log('[AmbX] HARDWARE MODE: backend captures audio');
        } else {
          if (!navigator?.mediaDevices?.getUserMedia) {
            throw new Error(
              'Microphone access not available. Please use HTTPS or localhost, and ensure your browser supports getUserMedia.'
            );
          }
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              channelCount: 1,
              sampleRate: { ideal: 48000 },
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          });
          streamRef.current = stream;
        }

        const wsUrl = getWebSocketUrl(`/asr?session_id=${sessionId}`);
        const ws = new WebSocket(wsUrl);

        await new Promise<void>((resolve, reject) => {
          ws.onopen = () => {
            setState('listening');
            startTranscriptListening();
            onStart?.();
            resolve();
          };
          ws.onerror = () => reject(new Error('WebSocket connection failed'));
          setTimeout(() => reject(new Error('WebSocket connection timeout')), 5000);
        });

        const applyTranscriptPayload = (data: Record<string, unknown>) => {
          let linesToShow =
            (Array.isArray(data.full_lines) ? data.full_lines : null) ||
            (Array.isArray(data.lines) ? data.lines : null) ||
            (Array.isArray(data.segments) ? data.segments : null);
          if (!linesToShow && typeof data.transcript === 'string' && String(data.transcript).trim()) {
            linesToShow = [{ speaker: 0, text: data.transcript, start: 0, end: 0 }];
          }
          if (linesToShow && Array.isArray(linesToShow) && linesToShow.length > 0) {
            (window as unknown as { __whisperLiveKitData?: unknown }).__whisperLiveKitData = {
              ...data,
              lines: linesToShow,
            };
            (window as unknown as { __whisperLiveKitSessionId?: string }).__whisperLiveKitSessionId =
              sessionId != null ? String(sessionId).toLowerCase() : undefined;
            setStatusMessage(`Transcribing… (${linesToShow.length} segments)`);
          } else if (data.status === 'no_audio_detected') {
            setStatusMessage('No audio detected');
          }
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data as string) as Record<string, unknown>;
            if (typeof data.sarvam_post_stop_batch_diarization === 'boolean') {
              postStopBatchUiRef.current = data.sarvam_post_stop_batch_diarization;
            }

            if (
              data.type === 'final_diarized_transcript' ||
              data.lines !== undefined ||
              data.full_lines !== undefined ||
              data.transcript !== undefined ||
              data.segments !== undefined
            ) {
              applyTranscriptPayload(data);
            } else if (data.type === 'config') {
              setStatusMessage('Connected — waiting for speech');
            } else if (data.type === 'error') {
              setError(String((data as { message?: string }).message ?? 'Error'));
              setStatusMessage('');
            }
          } catch (err) {
            console.error('[AmbX] Failed to parse message:', err);
          }
        };

        ws.onclose = () => {
          if (userStoppedRef.current) {
            userStoppedRef.current = false;
            return;
          }
          if (stateRef.current === 'listening') {
            reconnectTimeoutRef.current = window.setTimeout(() => {
              if (stateRef.current === 'listening') {
                void startListeningRef.current(true);
              }
            }, 3000);
          }
        };

        websocketRef.current = ws;

        // Send a tiny silent PCM frame every 25s to prevent proxy/nginx idle-close.
        // Must be binary non-empty: receive_bytes() rejects text; len==0 is end-of-stream signal.
        if (keepaliveIntervalRef.current) clearInterval(keepaliveIntervalRef.current);
        keepaliveIntervalRef.current = window.setInterval(() => {
          const w = websocketRef.current;
          if (w && w.readyState === WebSocket.OPEN) {
            try { w.send(new Uint8Array(4).buffer); } catch { /* ignore */ }
          }
        }, 25_000);

        if (audioSourceType === 'browser' && streamRef.current) {
          // Capture before any await: stopListening / Strict Mode can clear streamRef while addModule runs.
          const micStream = streamRef.current;
          const audioContext = new AudioContext();
          await audioContext.audioWorklet.addModule('/audio/pcm_worklet.js');
          const source = audioContext.createMediaStreamSource(micStream);
          const workletNode = new AudioWorkletNode(audioContext, 'pcm-forwarder', {
            numberOfInputs: 1,
            numberOfOutputs: 0,
            channelCount: 1,
          });
          source.connect(workletNode);

          const recorderWorker = new Worker('/audio/recorder_worker.js');
          recorderWorker.postMessage({
            command: 'init',
            config: { sampleRate: audioContext.sampleRate, targetSampleRate: 16000 },
          });

          workletNode.port.onmessage = (e: MessageEvent) => {
            const raw = e.data;
            const arrayBuffer = raw instanceof ArrayBuffer ? raw : (raw as { buffer: ArrayBuffer }).buffer;
            recorderWorker.postMessage({ command: 'record', buffer: arrayBuffer }, [arrayBuffer]);
          };

          recorderWorker.onmessage = (e: MessageEvent) => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send((e.data as { buffer: ArrayBuffer }).buffer);
            }
          };

          mediaRecorderRef.current = { audioContext, source, workletNode, recorderWorker };
        } else if (audioSourceType === 'hardware') {
          setStatusMessage('Hardware microphone active — waiting for speech');
        }
      } catch (err: unknown) {
        console.error('[AmbX] startListening failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to start listening');
        stopListening();
      }
    },
    [sessionId, audioSourceType, onStart, startTranscriptListening, stopListening]
  );

  startListeningRef.current = startListening;

  const stopListeningRef = useRef(stopListening);
  stopListeningRef.current = stopListening;

  useEffect(() => {
    return () => {
      stopListeningRef.current();
    };
  }, []);

  const handleToggle = () => {
    if (state === 'idle') {
      void startListening(false);
    } else if (state === 'listening') {
      stopListening();
    }
  };

  const idleBg = '#e3f2fd';
  const idleFg = '#1565c0';
  const listenColor = '#009688';
  const processingColor = '#ff9800';

  const micTooltip =
    disabled
      ? 'Disabled'
      : state === 'idle'
        ? 'Start ambient listening'
        : state === 'processing'
          ? processingProgress?.label ?? 'Processing conversation…'
          : awaitingWsClose
            ? 'Finishing…'
            : 'Stop listening';

  return (
    <Box>
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            position: 'fixed',
            bottom: 100,
            left: 20,
            zIndex: 1400,
            maxWidth: 320,
          }}
        >
          {error}
        </Alert>
      )}

      {statusMessage && state === 'listening' && (
        <Alert
          severity="info"
          sx={{
            position: 'fixed',
            bottom: 100,
            left: 20,
            zIndex: 1400,
            maxWidth: 300,
          }}
        >
          {statusMessage}
        </Alert>
      )}

      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: 24,
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {state === 'listening' && (
          <Paper
            elevation={2}
            sx={{
              px: 1.5,
              py: 0.5,
              bgcolor: 'rgba(0,0,0,0.8)',
              color: 'white',
              borderRadius: 2,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
            }}
          >
            I am listening
          </Paper>
        )}

        {state === 'processing' && (
          <Paper
            elevation={2}
            sx={{
              px: 1.5,
              py: 0.75,
              bgcolor: 'rgba(255,152,0,0.95)',
              color: 'white',
              borderRadius: 2,
              fontSize: '0.75rem',
              minWidth: 200,
              maxWidth: 280,
            }}
          >
            <Box component="span" sx={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.35 }}>
              {processingProgress?.label ?? 'Processing conversation…'}
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, processingProgress?.percent ?? 8))}
              sx={{
                mt: 0.75,
                height: 5,
                borderRadius: 1,
                bgcolor: 'rgba(255,255,255,0.35)',
                '& .MuiLinearProgress-bar': { bgcolor: 'white' },
              }}
              aria-label="Processing progress"
            />
          </Paper>
        )}

        <Tooltip title={micTooltip}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton
              onClick={handleToggle}
              disabled={disabled || state === 'processing' || awaitingWsClose}
              sx={{
                width: 64,
                height: 64,
                bgcolor: state === 'idle' ? idleBg : state === 'processing' ? processingColor : listenColor,
                color: state === 'idle' ? idleFg : 'white',
                boxShadow:
                  state === 'listening'
                    ? `0 0 20px ${listenColor}, 0 4px 12px rgba(0,0,0,0.3)`
                    : state === 'processing'
                      ? `0 0 20px ${processingColor}, 0 4px 12px rgba(0,0,0,0.3)`
                      : '0 4px 12px rgba(0,0,0,0.3)',
                border: `3px solid ${state === 'idle' ? idleFg : state === 'processing' ? processingColor : listenColor}`,
                transition: 'all 0.3s ease',
                animation:
                  state === 'listening' || state === 'processing'
                    ? 'pulseFloat 2s ease-in-out infinite'
                    : 'none',
                position: 'relative',
                zIndex: 1,
                '&:hover': {
                  transform: 'scale(1.1)',
                },
                '&.Mui-disabled': {
                  bgcolor: '#e0e0e0',
                  borderColor: '#bdbdbd',
                  color: '#757575',
                  boxShadow: 'none',
                },
                '@keyframes pulseFloat': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                },
              }}
            >
              {state === 'idle' ? (
                <Box
                  component="img"
                  src="/assets/mic_start_icon.png"
                  alt="Start listening"
                  sx={{ width: 36, height: 36, objectFit: 'contain' }}
                />
              ) : state === 'processing' ? (
                <CircularProgress size={32} sx={{ color: 'white' }} aria-label="Processing" />
              ) : (
                <Box
                  component="img"
                  src="/assets/mic_icon.png"
                  alt="Listening"
                  sx={{ width: 36, height: 36, objectFit: 'contain' }}
                />
              )}
            </IconButton>

            {state === 'listening' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  border: `2px solid ${listenColor}`,
                  pointerEvents: 'none',
                  animation: 'pulseRing 2s ease-in-out infinite',
                  '@keyframes pulseRing': {
                    '0%': { opacity: 1, transform: 'scale(1)' },
                    '100%': { opacity: 0, transform: 'scale(1.5)' },
                  },
                }}
              />
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}
